import { evaluateRouteAccess, type RouteDecision } from './authz.js';
import type { AppContext, AuthSession, LeagueMembership, NavItem } from './types.js';

const BASE_NAV: NavItem[] = [
  { id: 'dashboard', path: '/leagues', label: 'Dashboard' },
  { id: 'standings', path: '/leagues/{leagueId}/standings', label: 'Standings' },
  { id: 'matches', path: '/leagues/{leagueId}/matches', label: 'Schedule' },
  { id: 'roster', path: '/leagues/{leagueId}/roster', label: 'Roster' },
  { id: 'profile', path: '/leagues/{leagueId}/profile', label: 'My Profile' },
];

const ADMIN_NAV: NavItem[] = [
  {
    id: 'admin-home',
    path: '/leagues/{leagueId}/admin',
    label: 'Overview',
    adminOnly: true,
    section: 'league',
  },
  {
    id: 'admin-members',
    path: '/leagues/{leagueId}/admin/members',
    label: 'League Members',
    adminOnly: true,
    section: 'league',
  },
  {
    id: 'admin-invites',
    path: '/leagues/{leagueId}/admin/invites',
    label: 'Invites',
    adminOnly: true,
    section: 'league',
  },
  {
    id: 'admin-courses',
    path: '/leagues/{leagueId}/admin/courses',
    label: 'Courses',
    adminOnly: true,
    section: 'league',
  },
  {
    id: 'admin-seasons',
    path: '/leagues/{leagueId}/admin/seasons',
    label: 'Seasons',
    adminOnly: true,
    section: 'league',
  },
  {
    id: 'admin-season-players',
    path: '/leagues/{leagueId}/admin/seasons/{seasonId}/players',
    label: 'Season Players',
    adminOnly: true,
    section: 'season',
  },
  {
    id: 'admin-score-entry',
    path: '/leagues/{leagueId}/admin/score-entry',
    label: 'Score Entry',
    adminOnly: true,
    section: 'season',
  },
  {
    id: 'admin-match-days',
    path: '/leagues/{leagueId}/admin/match-days',
    label: 'Match Day Setup',
    adminOnly: true,
    section: 'season',
  },
];

function withContext(path: string, leagueId: string | null, seasonId: string | null): string {
  if (!leagueId) {
    return path;
  }

  return path
    .replace('{leagueId}', leagueId)
    .replace('{seasonId}', seasonId ?? '{seasonId}');
}

export class AppShellModel {
  private readonly context: AppContext = {
    session: null,
    memberships: [],
    activeLeagueId: null,
    activeSeasonId: null,
  };

  setSession(session: AuthSession | null): void {
    this.context.session = session;
  }

  setMemberships(memberships: LeagueMembership[]): void {
    this.context.memberships = memberships;
    if (
      this.context.activeLeagueId &&
      memberships.every((membership) => membership.leagueId !== this.context.activeLeagueId)
    ) {
      this.context.activeLeagueId = null;
    }
  }

  setActiveLeague(leagueId: string | null): void {
    this.context.activeLeagueId = leagueId;
  }

  setActiveSeason(seasonId: string | null): void {
    this.context.activeSeasonId = seasonId;
  }

  getContext(): AppContext {
    return {
      ...this.context,
      memberships: [...this.context.memberships],
    };
  }

  canAccess(path: string): RouteDecision {
    return evaluateRouteAccess(path, this.getContext());
  }

  getVisibleNav(): NavItem[] {
    const activeLeagueId = this.context.activeLeagueId;

    return BASE_NAV.map((item) => ({
      ...item,
      path: withContext(item.path, activeLeagueId, this.context.activeSeasonId),
    }));
  }

  getVisibleAdminNav(): NavItem[] {
    const activeLeagueId = this.context.activeLeagueId;
    const role =
      this.context.memberships.find((membership) => membership.leagueId === activeLeagueId)?.role ?? null;

    if (role !== 'admin') {
      return [];
    }

    return ADMIN_NAV.map((item) => ({
      ...item,
      path: withContext(item.path, activeLeagueId, this.context.activeSeasonId),
    }));
  }
}
